import React from 'react'
import * as XLSX from 'xlsx'

export interface PreprocessedData {
  validData: any[]
  validRowCount: number
  totalRowCount: number
  emptyRowCount: number
  errors: Array<{
    message: string
    count?: number
    details?: string
  }>
  isValid: boolean
  processedFile?: File
}

export interface ExcelPreprocessorProps {
  onSuccess: (data: PreprocessedData) => void
  onError: (error: string) => void
  onProcessingStart?: () => void
  onProcessingEnd?: () => void
}

export const useExcelPreprocessor = () => {
  /**
   * Processes an Excel file before sending to backend
   * @param file The Excel or CSV file to process
   * @returns Preprocessed data with validation results
   */
  const preprocessExcelFile = async (file: File): Promise<PreprocessedData> => {
    // Initialize result object
    const result: PreprocessedData = {
      validData: [],
      validRowCount: 0,
      totalRowCount: 0,
      emptyRowCount: 0,
      errors: [],
      isValid: true
    }

    try {
      // Read the file
      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, {
        type: 'array',
        cellDates: true,
        sheets: 0, // Only read first sheet
        cellNF: false,
        cellText: false
      })

      // Check if we have any sheets
      const firstSheetName = workbook.SheetNames[0]
      if (!firstSheetName) {
        result.errors.push({
          message: 'No sheets found in the workbook'
        })
        result.isValid = false
        return result
      }

      // Get the first sheet
      const worksheet = workbook.Sheets[firstSheetName]

      // Convert the sheet to JSON - explicitly type as array of arrays
      const jsonData = XLSX.utils.sheet_to_json<any[]>(worksheet, {
        header: 1,
        defval: '' // Default value for empty cells
      })

      // Track total rows
      result.totalRowCount = jsonData.length

      if (result.totalRowCount === 0) {
        result.errors.push({ message: 'Sheet is empty' })
        result.isValid = false
        return result
      }

      // Check header row
      let startRow = 0
      const headers = jsonData[0]

      // If headers match the expected format, skip the first row
      if (headers &&
          headers[0] === 'Student Email Address' &&
          headers[1] === 'Student First Name' &&
          headers[2] === 'Student Last Name') {
        startRow = 1
      }

      // Process each row, only taking first 3 columns
      const validRows: any[] = []
      let emptyRowCount = 0

      // Process data rows (starting from determined start row)
      for (let i = startRow; i < jsonData.length; i++) {
        const row = jsonData[i]

        // Only process the first 3 columns
        const processedRow = row.slice(0, 3)

        // Check if ALL THREE columns are empty
        const isRowCompletelyEmpty = processedRow.every((cell: any) => {
          if (cell === null || cell === undefined) return true
          if (typeof cell === 'string' && cell.trim() === '') return true
          if (typeof cell === 'object' && cell.text && typeof cell.text === 'string' && cell.text.trim() === '') return true
          return false
        })

        if (isRowCompletelyEmpty) {
          emptyRowCount++
          continue // Skip completely empty rows
        }

        // Add the row to valid rows - let backend do the detailed validation
        validRows.push({
          0: processedRow[0], // Keep original array indexes to match backend expectations
          1: processedRow[1],
          2: processedRow[2]
        })
      }

      // Update result
      result.validData = validRows
      result.validRowCount = validRows.length
      result.emptyRowCount = emptyRowCount

      // Create a new workbook with only valid data
      if (validRows.length > 0) {
        const newWorkbook = XLSX.utils.book_new()

        // Convert back to array form expected by backend
        const worksheetData = validRows.map(row => [row[0], row[1], row[2]])

        // Add header row back if it was detected
        if (startRow === 1) {
          worksheetData.unshift([headers[0], headers[1], headers[2]])
        }

        const newWorksheet = XLSX.utils.aoa_to_sheet(worksheetData)
        XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, 'Processed Data')

        // Convert the workbook to a blob
        const wbout = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

        // Create a new file
        result.processedFile = new File([blob], `processed_${file.name}`, { type: file.type })
      }

      return result
    } catch (error) {
      console.error('Excel preprocessing error:', error)
      result.errors.push({
        message: error instanceof Error ? error.message : 'Unknown error processing Excel file'
      })
      result.isValid = false
      return result
    }
  }

  return { preprocessExcelFile }
}

// React component wrapper for the preprocessor
const ExcelPreprocessor: React.FC<ExcelPreprocessorProps & { file: File }> = ({
  file,
  onSuccess,
  onError,
  onProcessingStart,
  onProcessingEnd
}) => {
  const { preprocessExcelFile } = useExcelPreprocessor()

  React.useEffect(() => {
    const processFile = async () => {
      try {
        if (onProcessingStart != null) onProcessingStart()

        const result = await preprocessExcelFile(file)

        if (result.isValid) {
          onSuccess(result)
        } else {
          onError(result.errors.map(e => e.message).join('. '))
        }
      } catch (error) {
        onError(error instanceof Error ? error.message : 'Unknown error processing file')
      } finally {
        if (onProcessingEnd != null) onProcessingEnd()
      }
    }

    processFile()
  }, [file, onSuccess, onError, onProcessingStart, onProcessingEnd, preprocessExcelFile])

  return null
}

export default ExcelPreprocessor
